/**
 * Guards for store actions that hit AWS Cost Explorer through the backend.
 *
 * Every Cost Explorer request is billed, so a screen must not send the same request twice while one is
 * pending, nor again shortly after it succeeded. The guard lives with the store action so every caller
 * (Dashboard, Cost & Usage tab, a StrictMode remount) is covered by one change.
 */

/** A successful cost fetch counts as fresh for this long; billing data itself refreshes only a few times a day. */
export const COST_FRESH_MS = 5 * 60 * 1000

export const isFresh = (fetchedAt: number | null): boolean =>
  fetchedAt !== null && Date.now() - fetchedAt < COST_FRESH_MS

export interface FetchGuard {
  /** Runs `task` unless one is already pending, in which case the pending promise is returned. */
  run: (task: (isCurrent: () => boolean) => Promise<void>) => Promise<void>
  /** Forgets the pending request and marks it stale, so a late response is ignored (sign-out, credential change). */
  reset: () => void
}

export const createFetchGuard = (): FetchGuard => {
  let inFlight: Promise<void> | null = null
  let generation = 0

  return {
    run: (task) => {
      if (inFlight) return inFlight
      const startedIn = generation
      const request: Promise<void> = task(() => startedIn === generation).finally(() => {
        if (inFlight === request) inFlight = null
      })
      inFlight = request
      return request
    },
    reset: () => {
      generation += 1
      inFlight = null
    },
  }
}
